import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'
import { ITableField } from '../index'
import { HTTPResponse } from '../../http/http.types'
import usePersistentState, { TPersistentStateStrategyType } from '../../hooks/usePersistentState'
import useHttpLoader from '../../../hooks/useHttpLoader'
import usePersistenceFields from '../usePersistenceFields'
import useResizeColumnPluginCtrl from '../plugins/resize/useResizeColumnPluginCtrl'
import useSkipFirstRenderEffect from '../../hooks/useSkipFirstRenderEffect'
import { IPaginationResponse } from '../../../types/global'

interface IProps<T extends Record<string, any>> {
  dataFetcher: (paginationParams: Record<string, string | number>) => Promise<HTTPResponse<IPaginationResponse<T[]>>>
  fields: ITableField<T, keyof T | string>[]
  itemKey: keyof T

  items: T[]
  setItems: Dispatch<SetStateAction<T[]>>
  tableKey: string

  forceLoad?: boolean
  serverSideOrder?: boolean
  storageType?: TPersistentStateStrategyType
}

const useTableWithRequest = <T extends Record<string, any>>(props: IProps<T>) => {
  const { wait, loading } = useHttpLoader()

  const [fields, setFields] = usePersistenceFields(props.fields, props.tableKey)
  const resizePluginCtrl = useResizeColumnPluginCtrl({
    columns: fields,
    tableKey: props.tableKey,
    columnsSetter: setFields,
  })

  const [page, setPage] = usePersistentState<number>(0, `${props.tableKey}/page`, props.storageType)
  const [paginationConfig, setPaginationConfig] = usePersistentState(
    {
      current: '',
      prev: '',
      next: '',
    },
    `${props.tableKey}/pagination-config`,
    props.storageType
  )

  const [loadedPage, setLoadedPage] = usePersistentState<number>(
    null,
    `${props.tableKey}/loadedPage`,
    props.storageType
  )
  const [visibleFields, setVisibleFields] = usePersistentState<Record<keyof T | string, boolean>>(
    getInitialFieldsVisibility(props.fields) as any,
    `${props.tableKey}/visibleFields`,
    props.storageType
  )

  const [itemsPerPage, _] = usePersistentState<number>(50, `${props.tableKey}/itemsPerPage`, props.storageType)
  const [totalItemsCount, setTotalItemsCount] = usePersistentState<number>(
    0,
    `${props.tableKey}/totalItemsCount`,
    props.storageType
  )

  // 0 - sort disabled, 1 - sort by asc, -1 = sort by desc
  const [orderingState, setOrderingState] = useState<Partial<Record<keyof T, -1 | 0 | 1>>>({})

  const orderingQuery = useMemo(() => {
    if (!props.serverSideOrder) return ''

    let orderingQuery = ''
    for (const field of props.fields) {
      if (orderingState[field.key] !== undefined && !field.clientSideOrdering && orderingState[field.key] !== 0) {
        orderingQuery += (orderingState[field.key] === 1 ? '' : '-') + field.key.toString()
      }
    }

    return orderingQuery
  }, [orderingState, props.serverSideOrder])

  const orderedItems = useMemo(() => {
    if (props.serverSideOrder && !props.fields.some((f) => f.clientSideOrdering)) {
      return props.items
    }

    const res = [...props.items]
    if (Object.values(orderingState).every((s) => s === undefined || s === 0)) return res

    res.sort((a, b) => {
      let result: number

      for (const field of props.fields) {
        if (props.serverSideOrder && !field.clientSideOrdering) continue

        if (orderingState[field.key] !== undefined && orderingState[field.key] !== 0) {
          result ||= comparator(
            field.sortGetter(a, field.key),
            field.sortGetter(b, field.key),
            orderingState[field.key] === 1 ? 'asc' : 'desc'
          )
        }
      }

      if (result === undefined) return 0
      return result
    })

    return res
  }, [orderingState, props.items, props.serverSideOrder])

  const visibleFieldsArr = useMemo(() => {
    return fields.filter((f) => visibleFields[f.key])
  }, [fields, visibleFields])

  useSkipFirstRenderEffect(() => {
    wait(props.dataFetcher({ limit: itemsPerPage }), (resp) => {
      if (resp.status === 'success') {
        setTotalItemsCount(resp.body.count)
        setLoadedPage(0)
        props.setItems(resp.body.results)
        setPaginationConfig((prev) => ({ ...prev, next: resp.body.next, prev: resp.body.previous }))
      }
    })
  }, [props.dataFetcher, itemsPerPage, orderingQuery])

  useEffect(() => {
    if ((loading || page === loadedPage) && !(totalItemsCount > 0 && props.items.length === 0) && !props.forceLoad) {
      return
    }

    const params: any = {}
    if (paginationConfig.current) {
      params.pageToken = paginationConfig.current
    }

    wait(props.dataFetcher(params), (resp) => {
      if (resp.status === 'success') {
        setLoadedPage(page)
        setTotalItemsCount(resp.body.count)
        setPaginationConfig((prev) => ({ ...prev, next: resp.body.next, prev: resp.body.previous }))
        props.setItems(resp.body.results)
      }
    })
  }, [page, paginationConfig.current])

  const handleSwap = useCallback(<K extends keyof T>(target: K, dropped: K) => {
    setFields((prev) => {
      const arr = [...prev]
      const targetIndex = arr.findIndex((e) => e.key === target)
      const droppedIndex = arr.findIndex((e) => e.key === dropped)
      if (targetIndex === droppedIndex) return prev

      if (targetIndex === -1 || droppedIndex === -1) return prev

      if (targetIndex > droppedIndex) {
        return [
          ...arr.slice(0, droppedIndex),
          ...arr.slice(droppedIndex + 1, targetIndex),
          arr[targetIndex],
          arr[droppedIndex],
          ...arr.slice(targetIndex + 1),
        ]
      }

      return [
        ...arr.slice(0, targetIndex),
        arr[droppedIndex],
        ...arr.slice(targetIndex).filter((it) => it.key !== dropped),
      ]
    })
  }, [])

  const handleVisibilityChange = useCallback(
    (key: keyof T | string, value: boolean) => {
      setVisibleFields({ ...visibleFields, [key]: value })
    },
    [visibleFields]
  )

  const handlePageNumberChange = useCallback((value: number) => {
    setPage((prev) => {
      if (prev > value) {
        setPaginationConfig((prev) => ({ ...prev, current: prev.prev, prev: '', next: prev.current }))
      } else {
        setPaginationConfig((prev) => ({ ...prev, current: prev.next, prev: prev.current, next: '' }))
      }

      return value
    })
  }, [])

  return {
    loading,
    orderedItems,
    itemsPerPage,
    pageNumber: page,
    onPageNumberChange: handlePageNumberChange,
    totalItemsCount,
    orderingState,
    onOrderingStateChange: setOrderingState,
    fields: visibleFieldsArr,
    onSwap: handleSwap,
    visibleFields,
    allFields: fields,
    handleVisibilityChange,
    resizePluginCtrl,
  }
}

const comparator = (a: any, b: any, sort: 'asc' | 'desc'): number => {
  if (typeof a === 'string' && typeof b === 'string') {
    if (sort === 'desc') return b.localeCompare(a)
    return a.localeCompare(b)
  }

  if (typeof a === 'number' && typeof b === 'number') {
    if (sort === 'desc') return b - a
    return a - b
  }

  return 0
}

const getInitialFieldsVisibility = <T extends Record<string, any>>(
  fields: ITableField<T, keyof T | string>[]
): Record<keyof T | string, boolean> => {
  const state: Record<keyof T | string, boolean> = {} as any
  // @ts-ignore
  for (const f of fields) state[f.key] = true

  return state
}

export default useTableWithRequest
